import { ChevronRight, Flame, Loader2, Pause, Play, Plus, RotateCcw, Save, Trash2, Users, X, Copy } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { StatusBadge, TierBadge, StorageModeBadge } from "@lexiun/org-admin";
import { usePlatformApi } from "../hooks/usePlatformApi";
import type { Organization, PlatformStats } from "../types/platform";
import { CreateOrgWizard } from "../components/platform/CreateOrgWizard";
import { PageShell, PageHeader } from "../components/layout/PageShell";
import { SoftDeleteOrgDialog } from "../components/platform/SoftDeleteOrgDialog";
import { ReinstateOrgDialog } from "../components/platform/ReinstateOrgDialog";
import { PermanentDeleteOrgDialog } from "../components/platform/PermanentDeleteOrgDialog";
import { CopyProviderSettingsDialog } from "../components/platform/CopyProviderSettingsDialog";

export function Organizations() {
  const api = usePlatformApi();
  const navigate = useNavigate();

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState("");
  // The status filter is URL-synced (?status=…) so the sidebar
  // "Recycle Bin" link (`/orgs?status=DELETED`) and deep links /
  // browser back-forward all stay coherent. ACTIVE is the default
  // landing filter — operators usually want active orgs first, and
  // a 60+-org dev list shouldn't dump TERMINATED rows up top. Two
  // pieces conspire to make that work:
  //
  //   1. A bare URL (no `status` param) inline-defaults statusFilter
  //      to "ACTIVE" — so the very first load() fetch is correctly
  //      filtered (no double-fetch race with an unfiltered list).
  //   2. The "All statuses" dropdown choice writes the explicit `ALL`
  //      sentinel, distinguishable from "bare URL" so it survives a
  //      refresh without bouncing back to ACTIVE.
  //
  // A mount effect canonicalises the URL bar to match (`?status=ACTIVE`
  // instead of bare /orgs) so back-button history is consistent.
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFromUrl = searchParams.get("status");
  // Canonicalise unknown status values back to ACTIVE so a typo or
  // stale link (e.g. `?status=All%20statuses` from a copy-pasted human
  // label) doesn't silently render an empty table. The valid set is
  // the OrgStatus enum plus the `ALL` sentinel (= no filter).
  const VALID_STATUS_VALUES = ["ALL", "ACTIVE", "SUSPENDED", "PROVISIONING", "DELETED", "TERMINATED"];
  const normalizedStatus =
    statusFromUrl && VALID_STATUS_VALUES.includes(statusFromUrl) ? statusFromUrl : "ACTIVE";
  const statusFilter = normalizedStatus === "ALL" ? "" : normalizedStatus;
  const setStatusFilter = useCallback(
    (next: string) => {
      setSearchParams(
        (sp) => {
          // Use the explicit `ALL` sentinel for the unfiltered view so
          // a refresh doesn't snap back to the ACTIVE default.
          sp.set("status", next || "ALL");
          return sp;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );
  // Mount-only: canonicalise bare /orgs → /orgs?status=ACTIVE with
  // replace:true so the auto-default doesn't pollute history. The
  // inline default above already drives the rendered filter; this
  // effect just keeps the URL bar honest. `has("status")` short-
  // circuits on Recycle Bin (`?status=DELETED`) and on explicit
  // `?status=ALL`.
  useEffect(() => {
    if (!searchParams.has("status")) {
      setSearchParams(
        (sp) => {
          sp.set("status", "ACTIVE");
          return sp;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [showCreate, setShowCreate] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  // Slice-3 dialog state — each holds the org being acted on (null = closed).
  const [softDeleteOrg, setSoftDeleteOrg] = useState<Organization | null>(null);
  const [copySourceOrg, setCopySourceOrg] = useState<Organization | null>(null);
  const [softDeleteBusy, setSoftDeleteBusy] = useState(false);
  const [softDeleteError, setSoftDeleteError] = useState<string | null>(null);
  const [reinstateOrg, setReinstateOrg] = useState<Organization | null>(null);
  const [reinstateBusy, setReinstateBusy] = useState(false);
  const [reinstateError, setReinstateError] = useState<string | null>(null);
  const [permanentOrg, setPermanentOrg] = useState<Organization | null>(null);

  const load = useCallback(
    async (append = false) => {
      try {
        if (!append) { setLoading(true); }
        const [orgRes, statsRes] = await Promise.all([
          api.listOrgs({
            status: statusFilter || undefined,
            limit: 50,
            cursor: append ? cursor : undefined,
          }),
          append ? Promise.resolve(null) : api.getStats(),
        ]);
        setOrgs((prev) => (append ? [...prev, ...orgRes.data] : orgRes.data));
        setHasMore(orgRes.meta.pagination.hasMore);
        setCursor(orgRes.meta.pagination.nextCursor);
        if (statsRes) { setStats(statsRes.data); }
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load organizations");
      } finally {
        setLoading(false);
      }
    },
    [api, statusFilter, cursor],
  );

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // SUSPEND / RE-ACTIVATE remain direct API calls (no reason required by
  // the server). Soft-delete (the row's "Move to Deleted" button) opens
  // the SoftDeleteOrgDialog so the operator can supply the mandatory
  // reason — slice-3 backend rejects 400 without it.
  const handleAction = async (
    orgId: string,
    action: "SUSPENDED" | "ACTIVE",
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    setActionLoading(orgId);
    setError(null);
    try {
      await api.updateOrg(orgId, { status: action });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${action.toLowerCase()} org`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSoftDelete = async (reason: string) => {
    if (!softDeleteOrg) return;
    setSoftDeleteBusy(true);
    setSoftDeleteError(null);
    try {
      await api.deleteOrg(softDeleteOrg.orgId, reason);
      setSoftDeleteOrg(null);
      await load();
    } catch (err) {
      setSoftDeleteError(err instanceof Error ? err.message : "Failed to soft-delete org");
    } finally {
      setSoftDeleteBusy(false);
    }
  };

  const handleReinstate = async (reason: string) => {
    if (!reinstateOrg) return;
    setReinstateBusy(true);
    setReinstateError(null);
    try {
      await api.reinstateOrg(reinstateOrg.orgId, reason);
      setReinstateOrg(null);
      await load();
    } catch (err) {
      setReinstateError(err instanceof Error ? err.message : "Failed to reinstate org");
    } finally {
      setReinstateBusy(false);
    }
  };

  const [editingOrg, setEditingOrg] = useState<string | null>(null);
  const [editTier, setEditTier] = useState("");
  const [editMaxUsers, setEditMaxUsers] = useState(0);
  const [editInvites, setEditInvites] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const handleEditTier = (org: Organization, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingOrg(org.orgId);
    setEditTier(org.settings.tier);
    setEditMaxUsers(org.settings.maxUsers);
    setEditInvites(org.allowOrgAdminInvitations ?? false);
  };

  const handleSaveSettings = async (orgId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSavingSettings(true);
    try {
      await api.updateOrg(orgId, { settings: { tier: editTier, maxUsers: editMaxUsers }, allowOrgAdminInvitations: editInvites });
      setEditingOrg(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const filtered = search
    ? orgs.filter(
        (o) =>
          o.name.toLowerCase().includes(search.toLowerCase()) ||
          o.slug.toLowerCase().includes(search.toLowerCase()),
      )
    : orgs;

  return (
    <PageShell>
      <PageHeader
        title="Organizations"
        subtitle={
          stats
            ? `${stats.orgs.active} active · ${stats.orgs.suspended} suspended · ${stats.orgs.provisioning} provisioning · ${stats.totalUsers} total users`
            : undefined
        }
        actions={
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" /> New Organization
          </button>
        }
      />

      <CreateOrgWizard
        open={showCreate}
        onOpenChange={setShowCreate}
        onProvisioned={() => { void load(); }}
      />

      {/* Filters */}
      <div className="mb-4 flex gap-3">
        <input
          type="text"
          placeholder="Search by name or slug..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-md border border-border bg-panel px-3 py-1.5 text-sm outline-none focus:border-primary dark:border-border dark:bg-panel"
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCursor(undefined);
          }}
          className="rounded-md border border-border bg-panel px-3 py-1.5 text-sm dark:border-border dark:bg-panel"
        >
          <option value="">All statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="SUSPENDED">Suspended</option>
          <option value="PROVISIONING">Provisioning</option>
          <option value="DELETED">Deleted</option>
          <option value="TERMINATED">Terminated</option>
        </select>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-12 text-sm text-faint">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading organizations...
        </div>
      ) : (
        <>
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wider text-muted dark:border-border dark:text-muted">
                <th className="pb-2 pr-4">Name</th>
                <th className="pb-2 pr-4">Slug</th>
                <th className="pb-2 pr-4">Status</th>
                <th className="pb-2 pr-4">Tier / Max Users</th>
                <th className="pb-2 pr-4">Storage</th>
                <th className="pb-2 pr-4">Actions</th>
                <th className="pb-2" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((org) => (
                <tr
                  key={org.orgId}
                  onClick={() => navigate(`/orgs/${org.orgId}`)}
                  className="cursor-pointer border-b border-border transition-colors hover:bg-panel-2 dark:border-border dark:hover:bg-panel"
                >
                  <td className="py-2.5 pr-4 font-medium">{org.name}</td>
                  <td className="py-2.5 pr-4 font-mono text-xs text-muted">
                    {org.slug}
                  </td>
                  <td className="py-2.5 pr-4">
                    <StatusBadge status={org.status} />
                  </td>
                  <td className="py-2.5 pr-4" onClick={(e) => e.stopPropagation()}>
                    {editingOrg === org.orgId ? (
                      <div className="flex items-center gap-2">
                        <select value={editTier} onChange={(e) => setEditTier(e.target.value)}
                          className="rounded border border-border bg-transparent px-1.5 py-0.5 text-xs dark:border-border">
                          <option value="free">free</option>
                          <option value="starter">starter</option>
                          <option value="professional">professional</option>
                          <option value="enterprise">enterprise</option>
                        </select>
                        <input type="number" value={editMaxUsers} onChange={(e) => setEditMaxUsers(Number(e.target.value) || 1)}
                          min={1} max={10000} className="w-16 rounded border border-border bg-transparent px-1.5 py-0.5 text-xs dark:border-border" />
                        <label className="flex items-center gap-1 text-[10px] text-muted" title="Allow this org's ORG_ADMINs to send/resend/revoke invitations (PLATFORM_ADMIN can always invite)">
                          <input type="checkbox" aria-label="Allow org-admin invitations" checked={editInvites}
                            onChange={(e) => setEditInvites(e.target.checked)} className="h-3 w-3" />
                          org-admin invites
                        </label>
                        <button type="button" onClick={(e) => handleSaveSettings(org.orgId, e)} disabled={savingSettings}
                          className="rounded p-0.5 text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20">
                          {savingSettings ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                        </button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); setEditingOrg(null); }}
                          className="rounded p-0.5 text-faint hover:bg-panel-2 dark:hover:bg-accent">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <button type="button" onClick={(e) => handleEditTier(org, e)} title="Edit tier & max users"
                        className="flex items-center gap-1.5 rounded px-1 py-0.5 hover:bg-accent">
                        <TierBadge tier={org.settings.tier} />
                        <span className="flex items-center gap-0.5 text-[10px] text-faint">
                          <Users className="h-2.5 w-2.5" />{org.settings.maxUsers}
                        </span>
                        <span
                          title={org.allowOrgAdminInvitations ? "Org-admin invitations: ENABLED" : "Org-admin invitations: disabled (LexPortal-only)"}
                          className={`rounded px-1 py-0.5 text-[9px] font-medium ${org.allowOrgAdminInvitations ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-panel-2 text-muted dark:bg-panel-2 dark:text-muted"}`}
                        >
                          {org.allowOrgAdminInvitations ? "invites: on" : "invites: off"}
                        </span>
                      </button>
                    )}
                  </td>
                  <td className="py-2.5 pr-4">
                    {/* S5 — storage allocation mode in its own column.
                        Read-only badge: the field is immutable post-provision
                        (UpdateOrgSchema deliberately excludes it). Pulled out
                        of the tier-edit button so clicks on the badge don't
                        accidentally open the tier editor. */}
                    <StorageModeBadge mode={org.settings.storageMode ?? "dedicated"} />
                  </td>
                  <td className="py-2.5 pr-4">
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      {actionLoading === org.orgId ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-faint" />
                      ) : (
                        <>
                          {org.status === "ACTIVE" && (
                            <button
                              type="button"
                              onClick={(e) => handleAction(org.orgId, "SUSPENDED", e)}
                              title="Suspend"
                              className="rounded p-1 text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
                            >
                              <Pause className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {org.status === "SUSPENDED" && (
                            <button
                              type="button"
                              onClick={(e) => handleAction(org.orgId, "ACTIVE", e)}
                              title="Reactivate"
                              className="rounded p-1 text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20"
                            >
                              <Play className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {org.status === "ACTIVE" && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCopySourceOrg(org);
                              }}
                              title="Copy AI provider settings from this org to another"
                              data-testid={`org-copy-settings-${org.orgId}`}
                              className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {(org.status === "ACTIVE" || org.status === "SUSPENDED") && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSoftDeleteError(null);
                                setSoftDeleteOrg(org);
                              }}
                              title="Move to Deleted (soft-delete; reversible via REINSTATE)"
                              data-testid={`org-soft-delete-${org.orgId}`}
                              className="rounded p-1 text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {org.status === "DELETED" && (
                            <>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setReinstateError(null);
                                  setReinstateOrg(org);
                                }}
                                title="Reinstate (DELETED → ACTIVE; restarts every soft-stopped runtime)"
                                data-testid={`deleted-orgs-reinstate-${org.orgId}`}
                                className="rounded p-1 text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPermanentOrg(org);
                                }}
                                title="Permanently delete (irreversible; two-phase confirm)"
                                data-testid={`deleted-orgs-permanent-${org.orgId}`}
                                className="rounded p-1 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                              >
                                <Flame className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                  <td className="py-2.5">
                    <ChevronRight className="h-4 w-4 text-faint dark:text-muted" />
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-sm text-faint">
                    No organizations found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {hasMore && (
            <button
              type="button"
              onClick={() => load(true)}
              className="mt-4 rounded-md border border-border px-4 py-1.5 text-sm text-muted transition-colors hover:bg-panel-2 dark:border-border dark:text-muted dark:hover:bg-accent"
            >
              Load more
            </button>
          )}
        </>
      )}

      {/* Slice-3 dialog mounts — kept inert when their `*Org` state is
          null so only one is ever interactive at a time. */}
      <SoftDeleteOrgDialog
        open={softDeleteOrg != null}
        onOpenChange={(o) => {
          if (!o) {
            setSoftDeleteOrg(null);
            setSoftDeleteError(null);
          }
        }}
        orgName={softDeleteOrg?.name ?? ""}
        orgSlug={softDeleteOrg?.slug ?? ""}
        onConfirm={handleSoftDelete}
        busy={softDeleteBusy}
        error={softDeleteError}
      />
      <CopyProviderSettingsDialog
        open={copySourceOrg != null}
        onOpenChange={(o) => {
          if (!o) setCopySourceOrg(null);
        }}
        orgs={orgs}
        initialSourceId={copySourceOrg?.orgId}
        onDone={() => void load()}
      />
      <ReinstateOrgDialog
        open={reinstateOrg != null}
        onOpenChange={(o) => {
          if (!o) {
            setReinstateOrg(null);
            setReinstateError(null);
          }
        }}
        orgName={reinstateOrg?.name ?? ""}
        orgSlug={reinstateOrg?.slug ?? ""}
        onConfirm={handleReinstate}
        busy={reinstateBusy}
        error={reinstateError}
      />
      <PermanentDeleteOrgDialog
        open={permanentOrg != null}
        onOpenChange={(o) => {
          if (!o) setPermanentOrg(null);
        }}
        orgId={permanentOrg?.orgId ?? null}
        onSuccess={() => {
          setPermanentOrg(null);
          void load();
        }}
      />
    </PageShell>
  );
}
